import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Bot, Cpu, Plug, Rocket, ChevronRight, ChevronLeft } from "lucide-react";
import { Link } from "wouter";

/**
 * Per-user localStorage key so different accounts on the same browser each
 * get their own tour state. A global key caused new users to silently inherit
 * the "tour already seen" flag from any previous user on the same device.
 */
function tourKey(userId: string | null | undefined): string {
  return userId ? `mb_guided_tour_v1_${userId}` : "mb_guided_tour_v1";
}

interface TourStep {
  icon: React.ReactNode;
  stepLabel: string;
  title: string;
  description: string;
  detail: React.ReactNode;
  accentColor: string;
  bgColor: string;
}

const STEPS: TourStep[] = [
  {
    icon: <Rocket className="w-6 h-6" />,
    stepLabel: "Welcome",
    title: "Welcome to your workspace!",
    description: "You're all set. Let's walk through the three things you need to do to launch your first AI chatbot.",
    accentColor: "text-violet-600",
    bgColor: "bg-violet-50",
    detail: (
      <div className="mt-4 rounded-lg bg-slate-50 border border-slate-100 p-4">
        <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Your setup checklist</p>
        <ol className="space-y-3">
          {[
            { n: 1, text: "Create your first chatbot" },
            { n: 2, text: "Configure an AI provider" },
            { n: 3, text: "Connect integrations" },
          ].map(({ n, text }) => (
            <li key={n} className="flex items-center gap-3 text-sm text-slate-700">
              <span className="w-6 h-6 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold flex items-center justify-center shrink-0">
                {n}
              </span>
              {text}
            </li>
          ))}
        </ol>
      </div>
    ),
  },
  {
    icon: <Bot className="w-6 h-6" />,
    stepLabel: "Create chatbot",
    title: "Create your first chatbot",
    description: "Chatbots are the core of your workspace — each one has its own personality, knowledge, and tools.",
    accentColor: "text-blue-600",
    bgColor: "bg-blue-50",
    detail: (
      <div className="mt-4 space-y-3 text-sm text-slate-600">
        <p>Go to <strong className="text-slate-800">Chatbots → Create Chatbot</strong> and give it a name.</p>
        <div className="grid grid-cols-2 gap-2">
          {["Persona & instructions", "Knowledge sources", "Tools (Jira, Slack…)", "Access mode"].map((item) => (
            <div key={item} className="flex items-center gap-2 text-xs bg-slate-50 rounded-md px-3 py-2 text-slate-700 border border-slate-100">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 shrink-0" />
              {item}
            </div>
          ))}
        </div>
        <Link href="/chatbots/create" className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline font-medium text-sm mt-1">
          Go to Chatbots <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    ),
  },
  {
    icon: <Cpu className="w-6 h-6" />,
    stepLabel: "AI Provider",
    title: "Configure your AI provider",
    description: "Your chatbot needs an AI model to generate responses. Add your API key to power it up.",
    accentColor: "text-emerald-600",
    bgColor: "bg-emerald-50",
    detail: (
      <div className="mt-4 space-y-3 text-sm text-slate-600">
        <p>Go to <strong className="text-slate-800">Settings → AI Providers</strong> and add your key.</p>
        <div className="space-y-1.5">
          {["OpenAI (GPT-4o, GPT-4o-mini)", "Azure OpenAI", "Anthropic Claude", "Google Gemini"].map((p) => (
            <div key={p} className="flex items-center gap-2 text-xs text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 shrink-0" />
              {p}
            </div>
          ))}
        </div>
        <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2.5 mt-1">
          <span className="shrink-0 mt-0.5">⚠️</span>
          <span>Without an AI provider key your chatbot cannot generate replies.</span>
        </div>
        <Link href="/ai-providers" className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline font-medium text-sm mt-1">
          Configure AI providers <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    ),
  },
  {
    icon: <Plug className="w-6 h-6" />,
    stepLabel: "Integrations",
    title: "Connect your integrations",
    description: "Link your tools so your chatbot can create tickets, look up records, and take actions on your behalf.",
    accentColor: "text-orange-600",
    bgColor: "bg-orange-50",
    detail: (
      <div className="mt-4 space-y-3 text-sm text-slate-600">
        <p>Go to <strong className="text-slate-800">Settings → Integrations</strong> to connect:</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            "Jira · Linear",
            "Zendesk · Freshdesk",
            "Slack · Teams",
            "SharePoint · Drive",
          ].map((item) => (
            <div key={item} className="flex items-center gap-2 text-xs bg-slate-50 rounded-md px-3 py-2 text-slate-700 border border-slate-100">
              <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
              {item}
            </div>
          ))}
        </div>
        <Link href="/integrations" className="inline-flex items-center gap-1.5 text-indigo-600 hover:text-indigo-700 hover:underline font-medium text-sm mt-1">
          Go to Integrations <ChevronRight className="w-3.5 h-3.5" />
        </Link>
      </div>
    ),
  },
];

/**
 * First-login guided tour dialog. Shows once per user per browser profile
 * (tracked by a user-scoped localStorage key). Super admins and users who
 * have already dismissed the tour see nothing.
 */
export function FirstLoginTour({ role, userId }: { role?: string | null; userId?: string | null }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    // Super admins manage the platform — they don't need the new-user tour.
    if (role === "super_admin") return;
    // Wait until the user identity is known so we read the correct per-user key.
    if (!userId) return;
    try {
      // Each user gets their own localStorage key (mb_guided_tour_v1_<userId>).
      // Do NOT fall back to a shared global key — a global key caused the tour
      // to be permanently suppressed for any new user on a browser that a
      // previous user had already dismissed it on. Per-user keys are self-
      // contained: if the key is not set, the tour has never been seen.
      if (!localStorage.getItem(tourKey(userId))) {
        setOpen(true);
      }
    } catch {
      // localStorage unavailable (private browsing restrictions) — skip silently.
    }
  }, [role, userId]);

  function dismiss() {
    try { localStorage.setItem(tourKey(userId), "1"); } catch {}
    setOpen(false);
  }

  const current = STEPS[step];
  const isLast = step === STEPS.length - 1;
  const isFirst = step === 0;
  const progressPct = ((step + 1) / STEPS.length) * 100;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) dismiss(); }}>
      <DialogContent
        className="max-w-lg p-0 overflow-hidden gap-0"
        aria-labelledby="tour-title"
        aria-describedby="tour-description"
      >
        {/* Progress bar */}
        <div className="h-1 bg-slate-100">
          <div
            className="h-full bg-indigo-500 transition-all duration-300 ease-out"
            style={{ width: `${progressPct}%` }}
            role="progressbar"
            aria-valuenow={step + 1}
            aria-valuemin={1}
            aria-valuemax={STEPS.length}
          />
        </div>

        <div className="p-6">
          {/* Step pills */}
          <div className="flex items-center gap-1.5 mb-5 overflow-x-auto pb-0.5">
            {STEPS.map((s, i) => (
              <button
                key={i}
                onClick={() => setStep(i)}
                aria-label={`Go to step ${i + 1}: ${s.stepLabel}`}
                className={[
                  "flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all",
                  i === step
                    ? "bg-indigo-600 text-white shadow-sm"
                    : i < step
                    ? "bg-indigo-100 text-indigo-600"
                    : "bg-slate-100 text-slate-400",
                ].join(" ")}
              >
                <span className="w-4 h-4 rounded-full border-[1.5px] flex items-center justify-center text-[10px] font-bold border-current">
                  {i < step ? "✓" : i + 1}
                </span>
                {s.stepLabel}
              </button>
            ))}
          </div>

          <DialogHeader className="space-y-0">
            {/* Icon + title */}
            <div className="flex items-start gap-4 mb-3">
              <div className={`w-12 h-12 rounded-xl ${current.bgColor} ${current.accentColor} flex items-center justify-center shrink-0 shadow-sm`}>
                {current.icon}
              </div>
              <div className="pt-0.5">
                <DialogTitle id="tour-title" className="text-lg font-semibold text-slate-900 leading-snug">
                  {current.title}
                </DialogTitle>
                <p className="text-xs text-slate-400 mt-0.5">
                  Step {step + 1} of {STEPS.length}
                </p>
              </div>
            </div>

            <DialogDescription id="tour-description" className="text-sm text-slate-600 leading-relaxed text-left">
              {current.description}
            </DialogDescription>
          </DialogHeader>

          {/* Step detail content */}
          <div className="text-left">{current.detail}</div>

          {/* Navigation */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setStep((s) => s - 1)}
              disabled={isFirst}
              className="text-slate-500 hover:text-slate-700"
              aria-label="Previous step"
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Back
            </Button>

            <div className="flex items-center gap-2">
              {!isLast && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={dismiss}
                  className="text-slate-400 hover:text-slate-600 text-xs"
                >
                  Skip tour
                </Button>
              )}
              {isLast ? (
                <Button
                  size="sm"
                  onClick={dismiss}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5"
                  data-testid="button-tour-finish"
                >
                  Get started <Rocket className="w-3.5 h-3.5 ml-1.5" />
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setStep((s) => s + 1)}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white px-5"
                  data-testid="button-tour-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
